import React from 'react'
import ApplicationResponsiveModal from './ApplicationResponsiveModal'
import {formatAustralianMobileInput, normalizeAustralianMobile} from './applicationPersonUtils'

const RELATIONSHIP_OPTIONS = [
  {label: 'Parent', value: 'parent'},
  {label: 'Sibling', value: 'sibling'},
  {label: 'Other family', value: 'other family'},
  {label: 'Friend', value: 'friend'},
  {label: 'Other', value: 'other'},
]

export type ApplicationPersonModalErrors = {
  fullName?: string
  mobileNumber?: string
  email?: string
  relationship?: string
}

type ApplicationPersonModalProps = {
  title: string
  submitLabel: string
  isOpen: boolean
  isSubmitting?: boolean
  submitDisabled?: boolean
  onClose: () => void
  onSubmit: () => void
  fullName: string
  mobileNumber: string
  onFullNameChange: (value: string) => void
  onMobileNumberChange: (value: string) => void
  errors?: ApplicationPersonModalErrors
  description?: string
  email?: string
  onEmailChange?: (value: string) => void
  showEmail?: boolean
  relationship?: string
  relationshipLabel?: string
  onRelationshipChange?: (value: string) => void
  closeAriaLabel?: string
  submitError?: string | null
}

export const ApplicationPersonModal: React.FC<ApplicationPersonModalProps> = ({
  title,
  submitLabel,
  isOpen,
  isSubmitting = false,
  submitDisabled = false,
  onClose,
  onSubmit,
  fullName,
  mobileNumber,
  onFullNameChange,
  onMobileNumberChange,
  errors,
  description,
  email = '',
  onEmailChange,
  showEmail = false,
  relationship = '',
  relationshipLabel = 'Relationship',
  onRelationshipChange,
  closeAriaLabel = 'Close dialog',
  submitError,
}) => {
  if (!isOpen) {
    return null
  }

  return (
    <ApplicationResponsiveModal
      ariaLabelledBy='application-person-modal-title'
      closeAriaLabel={closeAriaLabel}
      onClose={onClose}
      modalClassName='application-household-modal'
      closeButtonClassName='application-household-modal-close'
      mobileAnimationMs={380}
      swipeTarget='sheet'
    >
      <div className='application-household-modal-body'>
        <h2 id='application-person-modal-title' className='application-household-modal-title mb-3'>
          {title}
        </h2>

        {description && <p className='application-household-modal-subtitle'>{description}</p>}

        <div className='mt-4 mb-3'>
          <label className='form-label fw-semibold'>Full Name</label>
          <input
            value={fullName}
            onChange={(event) => onFullNameChange(event.target.value)}
            className={`form-control ${errors?.fullName ? 'is-invalid' : ''}`}
            placeholder='Enter full name'
          />
          {errors?.fullName && <div className='text-danger small mt-1'>{errors.fullName}</div>}
        </div>

        <div className='mb-3'>
          <label className='form-label fw-semibold'>Mobile Number</label>
          <input
            value={formatAustralianMobileInput(mobileNumber)}
            onChange={(event) =>
              onMobileNumberChange(normalizeAustralianMobile(event.target.value).slice(0, 10))
            }
            className={`form-control ${errors?.mobileNumber ? 'is-invalid' : ''}`}
            placeholder='0412 345 678'
          />
          {errors?.mobileNumber && (
            <div className='text-danger small mt-1'>{errors.mobileNumber}</div>
          )}
        </div>

        {showEmail && onEmailChange && (
          <div className='mb-3'>
            <label className='form-label fw-semibold'>Email</label>
            <input
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              className={`form-control ${errors?.email ? 'is-invalid' : ''}`}
              placeholder='email@example.com'
            />
            {errors?.email && <div className='text-danger small mt-1'>{errors.email}</div>}
          </div>
        )}

        {onRelationshipChange && (
          <div className='mb-3'>
            <label className='form-label fw-semibold'>{relationshipLabel}</label>
            <select
              value={relationship}
              onChange={(event) => onRelationshipChange(event.target.value)}
              className={`form-select ${errors?.relationship ? 'is-invalid' : ''}`}
            >
              <option value=''>Select relationship</option>
              {RELATIONSHIP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors?.relationship && (
              <div className='text-danger small mt-1'>{errors.relationship}</div>
            )}
          </div>
        )}

        {submitError && (
          <div className='text-danger small mt-3' role='alert'>
            {submitError}
          </div>
        )}

        <div className='d-flex gap-2 mt-3'>
          <button type='button' className='btn btn-light' style={{minWidth: 150}} onClick={onClose}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-doora flex-fill'
            onClick={onSubmit}
            disabled={isSubmitting || submitDisabled}
          >
            {isSubmitting ? ' Saving...' : submitLabel}
          </button>
        </div>
      </div>
    </ApplicationResponsiveModal>
  )
}
